// =============================================================================
// Frontend Error Reporter — ships errors/warns to /api/log/frontend-error,
// which forwards them through winston → Logstash → Elasticsearch →
// ElastAlert → Telegram.
//
// Design:
//   • Vanilla TS, zero deps — bundle cost is ~1.5 KB gzipped.
//   • Errors batched (≤3 per flush) and flushed every 3 s, OR immediately
//     when the queue fills. Uses `keepalive` so an in-flight POST survives
//     the page unloading. Falls back to `navigator.sendBeacon` on pagehide.
//   • Init runs in `requestIdleCallback` so it never blocks first paint.
//   • Client-side dedup by fingerprint (last 60 s) — a noisy infinite loop
//     can't drown out other errors before the server-side rate-limit kicks in.
//   • Aggressive drop list mirrors Sentry's beforeSend filter (ResizeObserver,
//     extension origins, Script error, AbortError, etc) so we don't ship
//     known-noisy events that don't help diagnose anything.
//   • Demo-mode bypass + session-expired short-circuit so logged-out users
//     don't flood the pipeline.
//
// Public API (intentionally Sentry-shaped so callsites are nearly identical):
//   initErrorReporter({ endpoint?, release?, enabled? })
//   captureException(err, ctx?)
//   captureMessage(msg, level?, ctx?)
// =============================================================================

import { getRecentRoutes } from "./recent-routes"
import { addBreadcrumb, clearBreadcrumbs, getBreadcrumbs, type Breadcrumb } from "./breadcrumbs"
import { initDeviceContext, getDeviceContext } from "./device-info"

type Level = "error" | "warn"

export interface ReporterContext {
  /** Override the route_path inferred from window.location.pathname. */
  route?: string
  /** Free-form route grouping tag (e.g. "trade", "admin/users"). */
  routeTag?: string
  /** Correlate with backend logs — pass the same id you sent to the API. */
  requestId?: string
  /** Persona override; auto-detected from pathname when omitted. */
  persona?: "admin" | "trade" | "superadmin" | "anonymous"
  /**
   * Identifier for the UI component / action that surfaced the error
   * (e.g. "admin_client_form_save"). Stored as a top-level Kibana field
   * so error volume can be grouped by component without parsing routeTag.
   */
  component?: string
  /** Arbitrary extra data — currently unused server-side, kept for forward-compat.
   *  NOTE: this is dropped on the wire. Anything that must reach Kibana needs a
   *  first-class field here and a matching whitelist entry in log.controller.js. */
  extra?: Record<string, unknown>
  /**
   * What automatic recovery did about this error (see lib/chunk-recovery.ts:
   * "reloading" | "deferred-critical" | "deferred-exhausted" | "offline").
   * Indexed server-side, because without it a chunk failure that silently
   * healed itself and one that left a trader stranded look identical in Kibana.
   */
  recoveryOutcome?: string
  /**
   * Aggregated connectivity-outage summary (see lib/net-journal.ts). Indexed
   * server-side into dedicated `outage_*` facets so duration / attempt counts /
   * which probe rung failed are all aggregatable in Kibana.
   */
  outage?: Record<string, unknown>
  /** Per-attempt trail belonging to `outage` — the minute-by-minute record of
   *  what the device tried while it couldn't reach us. */
  netJournal?: unknown[]
  /**
   * Report even after `session-expired` has halted the reporter. Reserved for
   * the outage summary: an outage that ENDED in a forced logout is precisely
   * the case we most need to see, and the halt would otherwise eat it.
   */
  bypassHalt?: boolean
  /**
   * Skip the 60 s client-side fingerprint dedup.
   *
   * Reserved for the connectivity-outage summary. That event is emitted from a
   * single fixed callsite with a fixed component, so its fingerprint is
   * identical for every outage — meaning two outages resolving inside the same
   * minute would silently collapse to one, and the second one's journal is
   * already cleared by then so the trail is gone with it. A flapping
   * wifi↔cellular connection produces exactly that pattern, which is the case
   * we least want to lose. Safe to exempt: an outage has to open on a failed
   * request and close on a successful one, so volume is bounded by real
   * traffic, and the queue/outbox caps still apply.
   */
  skipDedup?: boolean
  /**
   * Flush immediately instead of waiting for the 3 s scheduled flush that
   * `warn` normally rides.
   *
   * For a `warn` that is genuinely one-per-incident — the connectivity-outage
   * summary — those 3 seconds are a window in which the event lives only in the
   * in-memory queue. The write-ahead outbox copy is written by flushNow(), so
   * until that runs nothing on disk knows the incident happened, and
   * net-journal has by then handed over its own durable copy. Flushing eagerly
   * closes the window; the cost is one extra request per incident.
   */
  urgent?: boolean
}

export interface InitOptions {
  endpoint?: string
  release?: string
  enabled?: boolean
}

interface QueuedEvent {
  /**
   * Idempotency key, minted once at capture and carried through the outbox.
   *
   * Delivery is at-least-once and cannot be made otherwise: when the POST
   * response is lost (10 s abort, page unload, dead radio) the client has no
   * way to learn the server already stored the event, so the write-ahead copy
   * stays and is re-sent. Measured on prod 2026-08-14: one capture from trader
   * 24440 landed 36 times over 116 minutes — 36 Telegram alerts for a single
   * incident. The id lets the pipeline collapse those back into one document.
   */
  event_id: string
  name: string
  message: string
  stack?: string
  level: Level
  release: string
  url: string
  route: string
  persona: string
  recent_routes: string[]
  /** Snapshot of breadcrumbs at the moment the error was captured. Frozen
   *  here so the trail reflects "state at crash" even if more events arrive
   *  before the flush actually goes out. */
  breadcrumbs: Breadcrumb[]
  user_agent: string
  request_id?: string
  /** Identifier of the UI component / action that surfaced the error. */
  component?: string
  /** What automatic recovery did about this error — see ReporterContext. */
  recovery_outcome?: string
  /** `navigator.onLine` + optional Network Information API hints captured
   *  at the moment of the error — e.g. "online", "offline", "online:4g",
   *  "online:slow-2g". Backend splits the suffix into network_effective_type. */
  network_status: string
  /** True inside the packaged APK/iOS app; false in any browser or PWA.
   *  Set synchronously so event #1 is correctly tagged even before the
   *  @capacitor/device plugin bridge resolves. */
  is_native: boolean
  /** Native-only device details populated asynchronously by device-info.ts
   *  via @capacitor/device. Absent on web/PWA — server-side UA parsing
   *  (log.controller.js#parseUserAgent) covers browser contexts. */
  device_manufacturer?: string
  device_model?: string
  device_os_version?: string
  webview_version?: string
  app_version?: string
  is_virtual?: boolean
  android_sdk_int?: number
  /** Epoch ms at the moment of capture. Without this the server has no choice
   *  but to stamp @timestamp at INGEST time — and for exactly the events that
   *  matter here (the ones that couldn't be delivered when they happened) that
   *  is wrong by however long the outage lasted. Measured lags on prod before
   *  this field existed: 4 s … 703 s … one event 15.9 HOURS late. Every
   *  offline-incident timeline read out of Kibana was reading delivery time. */
  client_ts: number
  /** Trader id read from localStorage at CAPTURE time. Correlation hint only —
   *  the authoritative `user_id` is resolved server-side from the session. See
   *  currentUserId(). Empty when nobody was logged in. */
  client_user_id: string
  /** Aggregated outage summary — present only on `connectivity_outage`. */
  outage?: Record<string, unknown>
  /** Per-attempt trail belonging to `outage`. */
  net_journal?: unknown[]
  /** Local-only — used for dedup before posting. */
  __fingerprint: string
}

// ─── Tunables ────────────────────────────────────────────────────────────────
const QUEUE_SOFT_LIMIT = 3
// Hard cap so a runaway error loop can't grow the queue without bound between
// the eager-flush check and an actual scheduled flush. At this cap we drop the
// OLDEST queued events — the newest are more likely to be diagnostic.
const QUEUE_HARD_CAP = 100
const FLUSH_INTERVAL_MS = 3000
const DEDUP_WINDOW_MS = 60 * 1000
const DEDUP_MAP_MAX = 50
const MAX_STACK_FRAMES_CLIENT = 50 // backend keeps only top 5; we send more so symbolication has options

// ─── Drop list — events we never ship (mirror of Sentry beforeSend) ─────────
const DROP_MESSAGE_SUBSTRINGS: readonly string[] = [
  "ResizeObserver loop",
  "ResizeObserver loop completed",
  "Non-Error promise rejection captured",
  "Script error.",
  "Load failed", // Safari network error noise
]
const DROP_URL_PATTERNS: readonly RegExp[] = [
  /^chrome-extension:\/\//i,
  /^moz-extension:\/\//i,
  /^safari-extension:\/\//i,
  /extensions\//i,
  /google\.com\/recaptcha/i,
  /gstatic\.com\/recaptcha/i,
]

// ─── Module state ────────────────────────────────────────────────────────────
let initialized = false
let enabled = true
let endpoint = "/api/log/frontend-error"
let release = "unknown"
let userAgent = ""

let queue: QueuedEvent[] = []
let flushTimer: ReturnType<typeof setTimeout> | null = null

// ─── Pre-init capture buffer ────────────────────────────────────────────────
// initErrorReporter() runs inside requestIdleCallback(boot, {timeout: 2000}) so
// it never blocks first paint. Anything captured before that landed on
// `!initialized` and was dropped on the floor, silently.
//
// That window is not theoretical: apiFetch starts issuing calls the moment
// React hydrates, and net-journal's noteRequestSuccess() resolves an
// outage carried over from a previous page load on the FIRST successful
// response. resolveOutage() clears its localStorage before calling
// captureMessage() — so the report was dropped here while the durable copy was
// already gone. Measured on this build: 6 of 6 outages that spanned a reload
// were destroyed, which is exactly the "user pressed Try Again mid-outage"
// case the outage journal exists to survive.
//
// Buffering closes it. `at` preserves the ORIGINAL capture time so a replayed
// event still carries client_ts from when it happened, not when it drained —
// re-stamping here would quietly reintroduce the ingest-time bug that client_ts
// was added to fix.
interface PendingCapture {
  name: string
  message: string
  stack: string
  level: Level
  ctx?: ReporterContext
  at: number
}
// Bounded so a page where init never runs can't leak. Sized for a busy boot:
// the trade shell can fire a dozen-plus ui_notification captures while
// hydrating, and the outage summary arrives AFTER them — a tight cap that
// refused new entries dropped exactly the event this buffer exists to save.
const PENDING_MAX = 50
let pending: PendingCapture[] = []

// Fingerprint → last-sent timestamp. Bounded by DEDUP_MAP_MAX with LRU eviction.
const dedup = new Map<string, number>()

// Set when the session-expired event fires so we stop reporting subsequent
// 401-driven errors that aren't actionable.
let halted = false

// ─── Helpers ─────────────────────────────────────────────────────────────────
function safe<T>(fn: () => T, fallback: T): T {
  try {
    return fn()
  } catch {
    return fallback
  }
}

function inDemoMode(): boolean {
  return safe(() => window.sessionStorage.getItem("demo_mode") === "true", false)
}

// ─── Capture-time identity ──────────────────────────────────────────────────
// The server attributes an event by resolving the session cookie on the
// incoming POST, which is correct but happens at DELIVERY time. Reports here
// are routinely delivered much later than they were captured — the outbox holds
// them until connectivity returns — and `triggerSessionExpiry` /
// the logout handler both `localStorage.removeItem("user")`. So an outage
// generated by a logged-in trader whose session ended before the report got out
// arrives attributed to `anonymous`, and that is exactly the incident we most
// want to see (the outage summary sets `bypassHalt` precisely so it survives a
// session-expiry cascade).
//
// Stamping the id at capture closes that. Deliberately a SEPARATE field from
// the server-resolved `user_id`: this value is client-supplied and therefore
// spoofable, so it is only ever a correlation hint and must never be treated as
// proof of identity.
let lastKnownUserId = ""

function currentUserId(): string {
  const id = safe(() => {
    const raw = window.localStorage.getItem("user")
    if (!raw) return ""
    const u = JSON.parse(raw) as { id?: unknown }
    return u && u.id !== undefined && u.id !== null ? String(u.id) : ""
  }, "")
  // Remember it. Logout clears `user` synchronously, but events captured after
  // that — the outage summary that resolves during the logout cascade — still
  // belong to the trader who was just here.
  if (id) lastKnownUserId = id.slice(0, 40)
  return lastKnownUserId
}

function detectPersona(pathname: string): string {
  if (!pathname) return "anonymous"
  if (pathname.startsWith("/admin")) return "admin"
  if (pathname.startsWith("/superadmin")) return "superadmin"
  if (pathname.startsWith("/trade")) return "trade"
  return "anonymous"
}

/**
 * Snapshot of connectivity at the moment of error. Combines `navigator.onLine`
 * (always available) with the Network Information API's `effectiveType`
 * (Chrome/Edge/Android — gives "4g", "3g", "slow-2g") when present.
 *
 * Format: "online", "offline", or "online:<effectiveType>".
 * The backend splits the colon-delimited suffix into network_effective_type
 * so each can be aggregated independently in Kibana.
 */
function snapshotNetworkStatus(): string {
  if (typeof navigator === "undefined") return "unknown"
  const online = typeof navigator.onLine === "boolean" ? navigator.onLine : true
  if (!online) return "offline"
  const conn = (navigator as unknown as { connection?: { effectiveType?: string } }).connection
  const eff = conn && typeof conn.effectiveType === "string" ? conn.effectiveType : ""
  return eff ? `online:${eff}` : "online"
}

function truncate(s: string | undefined, max: number): string {
  if (!s) return ""
  return s.length > max ? s.slice(0, max) : s
}

function isDroppedMessage(msg: string): boolean {
  return DROP_MESSAGE_SUBSTRINGS.some((needle) => msg.includes(needle))
}

function isDroppedSource(src: string | undefined): boolean {
  if (!src) return false
  return DROP_URL_PATTERNS.some((re) => re.test(src))
}

/**
 * Trim a stack to N frames so we don't ship 100-frame React/Next.js stacks
 * that mostly contain framework noise.
 */
function trimStack(stack: string | undefined): string {
  if (!stack) return ""
  const lines = stack.split("\n")
  if (lines.length <= MAX_STACK_FRAMES_CLIENT + 1) return stack
  return lines.slice(0, MAX_STACK_FRAMES_CLIENT + 1).join("\n")
}

/**
 * Cheap fingerprint of (name + top stack line + optional component). Used
 * for client-side dedup ONLY; the server computes its own (more stable)
 * fingerprint from the parsed top frame.
 *
 * Component is included so two distinct callsites that capture the SAME
 * underlying error (e.g. apiFetch's network_error path PLUS a form catch's
 * reportActionError) both ship — each with its own component context —
 * while a runaway loop at the SAME callsite still dedups within 60s.
 * Without this, the form-specific capture (more triageable) loses to the
 * api.ts fallback capture and the routeTag/component info is dropped.
 */
function clientFingerprint(name: string, stack: string, component?: string): string {
  // First non-empty stack line that isn't the message header.
  const lines = stack.split("\n")
  let top = ""
  for (const line of lines) {
    const t = line.trim()
    if (t && !t.startsWith(name)) {
      top = t
      break
    }
  }
  const suffix = component ? `|${component}` : ""
  return `${name}|${top}${suffix}`.slice(0, 240)
}

function shouldSuppress(fp: string): boolean {
  const now = Date.now()
  const last = dedup.get(fp)
  if (last !== undefined && now - last < DEDUP_WINDOW_MS) return true
  // LRU-ish: when we cross the cap, drop the oldest 10 entries by insertion order.
  if (dedup.size >= DEDUP_MAP_MAX) {
    let dropped = 0
    for (const key of dedup.keys()) {
      dedup.delete(key)
      if (++dropped >= 10) break
    }
  }
  dedup.set(fp, now)
  return false
}

// ─── Offline outbox (localStorage) ──────────────────────────────────────────
// Persistent fallback queue for events whose POST failed (network down,
// backend 5xx, etc.). Stored in localStorage so it survives page navigation
// and tab close. Drained via `navigator.sendBeacon` on the `online` event
// and on `pagehide`/`visibilitychange:hidden`, plus once on init to flush
// leftovers from a prior session.
//
// Why this exists: without it, a connection-lost event triggered by a
// network failure was silently dropped — the very same network failure
// that triggered the dialog also killed the report POST. The outbox closes
// that visibility gap so Kibana sees these events the moment the network
// returns.
//
// Performance footprint: the hot path (successful POST) does not touch the
// outbox at all. localStorage is only read/written on the error path or
// rare lifecycle events (online, pagehide, init). Both budgets below are
// hard caps; FIFO eviction trims from the head so the newest, most
// diagnostic events always win.
const OUTBOX_KEY = "__err_outbox_v1"
const OUTBOX_MAX_ENTRIES = 50
const OUTBOX_MAX_BYTES = 50 * 1024 // 50 KB — well under any browser quota

// Dedup bucket. The original key had NO time component, so a sustained outage
// collapsed to a single row: every repeat of the same
// `network_error / api_network_error / /trade/dashboard` triple after the first
// was dropped, and the surviving row carried neither an attempt count nor a
// duration. Bucketing by the minute keeps a runaway loop bounded (the 50-entry
// cap still applies) while preserving the shape of a long outage over time.
const OUTBOX_DEDUP_BUCKET_MS = 60 * 1000

interface OutboxEntry {
  body: string
  ts: number
  /** Correlates the write-ahead push with the delete-on-success below. */
  id: string
}

let outboxSeq = 0
function nextOutboxId(): string {
  outboxSeq += 1
  return `${Date.now().toString(36)}-${outboxSeq.toString(36)}`
}

function readOutbox(): OutboxEntry[] {
  try {
    const raw = window.localStorage.getItem(OUTBOX_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    const entries = parsed as OutboxEntry[]
    // ── Migration from the pre-write-ahead format ───────────────────────────
    // Entries written by the previous release are {body, ts} with no `id`.
    // Delete-on-success matches on `id`, so without this backfill those
    // entries would be delivered and then never removed — re-POSTing the same
    // stale events on every drain, forever, for every already-logged-in
    // trader who happens to have a non-empty outbox at upgrade time.
    let migrated = false
    for (const e of entries) {
      if (e && typeof e === "object" && !e.id) {
        e.id = nextOutboxId()
        migrated = true
      }
    }
    if (migrated) writeOutbox(entries)
    return entries
  } catch {
    return []
  }
}

function writeOutbox(entries: OutboxEntry[]): void {
  try {
    if (entries.length === 0) {
      window.localStorage.removeItem(OUTBOX_KEY)
      return
    }
    window.localStorage.setItem(OUTBOX_KEY, JSON.stringify(entries))
  } catch {
    // Quota exceeded / storage disabled (Safari private mode quirks) —
    // silently give up. We'd rather lose the report than throw inside an
    // already-failing error path.
  }
}

// Identity key for outbox dedup — derived from the fields that uniquely
// describe the error condition (NOT timestamps, breadcrumbs, request_ids).
// Two events with the same key represent the same underlying problem and
// only one copy is worth keeping in the outbox.
function outboxKeyForBody(body: string): string {
  try {
    const p = JSON.parse(body) as {
      name?: string
      message?: string
      level?: string
      component?: string
      routeTag?: string
      route?: string
      client_ts?: number
    }
    return [
      p.name || "",
      (p.message || "").slice(0, 160),
      p.level || "",
      p.component || "",
      p.routeTag || "",
      p.route || "",
      // Minute bucket — see OUTBOX_DEDUP_BUCKET_MS. Sourced from the entry's
      // own client_ts so re-keying an already-stored entry is stable.
      Math.floor((Number(p.client_ts) || 0) / OUTBOX_DEDUP_BUCKET_MS),
    ]
      .join("|")
      .slice(0, 320)
  } catch {
    return body.slice(0, 320)
  }
}

function pushOutbox(body: string, id?: string): string | null {
  if (typeof window === "undefined" || !window.localStorage) return null
  const entries = readOutbox()
  // Cross-session dedup. The in-memory dedup map in `enqueue` only protects
  // within one page session and one 60 s window. This adds a second layer
  // that survives reloads / new tabs (localStorage is shared per origin):
  // if the same error condition is already in the outbox, drop the new
  // copy. The first occurrence keeps its original timestamp so Kibana
  // shows when the issue actually started, not the most recent re-fire.
  const key = outboxKeyForBody(body)
  if (entries.some((e) => outboxKeyForBody(e.body) === key)) return null
  const entryId = id || nextOutboxId()
  entries.push({ body, ts: Date.now(), id: entryId })
  while (entries.length > OUTBOX_MAX_ENTRIES) entries.shift()
  let total = 0
  for (const e of entries) total += e.body.length
  while (total > OUTBOX_MAX_BYTES && entries.length > 0) {
    const dropped = entries.shift()
    if (dropped) total -= dropped.body.length
  }
  writeOutbox(entries)
  return entryId
}

// Delete-on-success half of the write-ahead pair. A miss is normal and cheap:
// the entry may have been deduped away at push time, or evicted by the caps.
function removeOutbox(id: string | null): void {
  if (!id) return
  if (typeof window === "undefined" || !window.localStorage) return
  const entries = readOutbox()
  if (entries.length === 0) return
  const remaining = entries.filter((e) => e.id !== id)
  if (remaining.length !== entries.length) writeOutbox(remaining)
}

/**
 * Drain the outbox.
 *
 * Two modes, because the two callers have fundamentally different guarantees
 * available to them:
 *
 *   beacon mode (`pagehide` / `visibilitychange:hidden`) — the page is dying,
 *     `fetch` promises will not survive, so `sendBeacon` is the only option.
 *     It is fire-and-forget and CANNOT confirm delivery, therefore entries are
 *     LEFT IN PLACE. Previously this path cleared the outbox on a truthy
 *     sendBeacon return — but that only means "the browser queued it", and
 *     with the origin unreachable the browser drops the payload later. That is
 *     how sank09's reports were destroyed: `navigator.onLine` was true so the
 *     offline guard didn't fire, he switched apps mid-outage, and the outbox
 *     was emptied without anything being delivered. Duplicates on the server
 *     are strictly better than silent loss — the backend fingerprints events,
 *     and a redelivered entry is dropped by that, whereas a lost one is gone.
 *
 *   fetch mode (`online` event, init, post-recovery) — the page is alive, so
 *     we can await a real response and delete only what the server confirmed
 *     with a 2xx. This is the path that actually empties the outbox.
 */
function drainOutbox(useBeacon = false): void {
  if (typeof window === "undefined") return
  // Skip drain when the device reports no link at all. This guard is only
  // reliable in the FALSE direction — `onLine === true` says nothing about
  // whether our origin is reachable, which is why fetch mode confirms with a
  // real response rather than trusting this flag.
  if (typeof navigator !== "undefined" && navigator.onLine === false) return
  const entries = readOutbox()
  if (entries.length === 0) return

  if (useBeacon) {
    if (typeof navigator === "undefined" || typeof navigator.sendBeacon !== "function") return
    for (const e of entries) {
      safe(() => {
        const blob = new Blob([e.body], { type: "application/json" })
        return navigator.sendBeacon(endpoint, blob)
      }, false)
    }
    // Intentionally NOT cleared — see the doc comment above.
    return
  }

  for (const e of entries) {
    safe(
      () =>
        fetch(endpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json", "X-App-Origin": "dhan-fx-web" },
          body: e.body,
          credentials: "same-origin",
          mode: "same-origin",
          // Bounded so a blackholed origin can't leave this promise pending
          // forever and pin the entry's memory for the life of the page.
          signal: AbortSignal.timeout(10000),
        })
          .then((res) => {
            // Confirmed delivery only. 4xx counts as delivered-and-rejected:
            // the server saw it and doesn't want it, so retrying forever would
            // just pin a poison entry at the head of the queue.
            if (res.ok || (res.status >= 400 && res.status < 500)) removeOutbox(e.id)
          })
          .catch(() => {
            /* still unreachable — leave it for the next drain */
          }),
      undefined
    )
  }
}

// ─── Transport ───────────────────────────────────────────────────────────────
function flushNow(useBeacon = false): void {
  if (queue.length === 0) return
  const batch = queue
  queue = []
  if (flushTimer) {
    clearTimeout(flushTimer)
    flushTimer = null
  }

  for (const evt of batch) {
    // Serialize per-event inside its own try so a single bad payload (e.g.
    // a stringified reason that turned out to contain a freshly-introduced
    // circular ref) can't abort the rest of the batch.
    let body: string
    try {
      body = JSON.stringify({
        // Idempotency key. MUST be inside the serialized body, not added per
        // attempt: the outbox stores this exact string and re-sends it
        // verbatim, so every re-delivery of one capture carries one id and the
        // pipeline can collapse them onto a single document.
        event_id: evt.event_id,
        ...(evt.recovery_outcome && { recovery_outcome: evt.recovery_outcome }),
        name: evt.name,
        message: evt.message,
        stack: evt.stack,
        level: evt.level,
        release: evt.release,
        url: evt.url,
        route: evt.route,
        persona: evt.persona,
        recent_routes: evt.recent_routes,
        breadcrumbs: evt.breadcrumbs,
        user_agent: evt.user_agent,
        request_id: evt.request_id,
        component: evt.component,
        network_status: evt.network_status,
        // Native device context. `is_native` is always sent (boolean facet
        // that ES needs to filter on); the rest are only sent when defined
        // to keep web-path payloads tiny — JSON.stringify drops undefined
        // keys but we're explicit to avoid depending on that.
        is_native: evt.is_native,
        ...(evt.device_manufacturer && { device_manufacturer: evt.device_manufacturer }),
        ...(evt.device_model && { device_model: evt.device_model }),
        ...(evt.device_os_version && { device_os_version: evt.device_os_version }),
        ...(evt.webview_version && { webview_version: evt.webview_version }),
        ...(evt.app_version && { app_version: evt.app_version }),
        ...(evt.is_virtual && { is_virtual: true }),
        ...(evt.android_sdk_int !== undefined && { android_sdk_int: evt.android_sdk_int }),
        client_ts: evt.client_ts,
        ...(evt.client_user_id && { client_user_id: evt.client_user_id }),
        ...(evt.outage && { outage: evt.outage }),
        ...(evt.net_journal && evt.net_journal.length > 0 && { net_journal: evt.net_journal }),
      })
    } catch {
      // Unserialisable — drop quietly and move on. We could ship a synthetic
      // "serialisation failed" event instead, but that risks recursion.
      continue
    }

    // sendBeacon is fire-and-forget — perfect for pagehide. Browser flushes
    // it from a background queue after the page is gone.
    if (
      useBeacon &&
      typeof navigator !== "undefined" &&
      typeof navigator.sendBeacon === "function"
    ) {
      // Beacon needs a Blob with the right Content-Type or the server sees an empty body.
      const blob = new Blob([body], { type: "application/json" })
      safe(() => navigator.sendBeacon(endpoint, blob), false)
      continue
    }

    // ── Write-ahead ─────────────────────────────────────────────────────────
    // Park the body in the outbox BEFORE attempting delivery, and remove it
    // only once the server confirms. The old ordering — push only from the
    // POST's .then/.catch — meant that between "left the queue" and "promise
    // settled" the event existed nowhere durable. Against a blackholed origin
    // that window is unbounded (no RST ever arrives), and anything that tears
    // the document down inside it destroys the event outright. That is what
    // happened to sank09: he pressed "Try Again" → window.location.reload()
    // → every pending report died with the page, having never been persisted.
    const outboxId = pushOutbox(body)

    // `keepalive: true` lets an in-flight POST survive page unload up to ~64 KB total.
    // We never await — the reporter must not block any caller.
    //
    // X-App-Origin tags this as a legitimate first-party browser POST so the
    // server-side app-origin guard (log.routes.js) lets it through. The
    // sendBeacon path above can't set custom headers, but it relies on the
    // browser-set `Origin` header which the same guard accepts as a fallback.
    //
    // The 10 s AbortSignal is what makes the .catch reachable at all. Without
    // it this fetch has NO timeout: against an unreachable origin it hangs
    // until the browser's own socket timeout (often minutes), so the failure
    // handling below simply never ran.
    safe(
      () =>
        fetch(endpoint, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-App-Origin": "dhan-fx-web",
          },
          body,
          credentials: "same-origin",
          keepalive: true,
          mode: "same-origin",
          signal: AbortSignal.timeout(10000),
        })
          .then((res) => {
            // Delivered (2xx) or permanently rejected (4xx — malformed body,
            // rate-limited origin guard): either way retrying is pointless.
            if (res.ok || (res.status >= 400 && res.status < 500)) removeOutbox(outboxId)
          })
          .catch(() => {
            /* transport failed — the write-ahead copy stands */
          }),
      undefined
    )
  }
}

function scheduleFlush() {
  if (flushTimer) return
  flushTimer = setTimeout(() => {
    flushTimer = null
    flushNow(false)
  }, FLUSH_INTERVAL_MS)
}

// ─── Public capture API ──────────────────────────────────────────────────────
/**
 * Mint the per-capture idempotency key. `crypto.randomUUID` where available
 * (every browser we support that can run the app bundle), otherwise a
 * timestamp+random pair — uniqueness only has to hold across one device's
 * events, since the server never compares ids between clients.
 */
function makeEventId(capturedAt: number): string {
  const uuid = safe(() => {
    const c = window.crypto as Crypto & { randomUUID?: () => string }
    return typeof c?.randomUUID === "function" ? c.randomUUID() : ""
  }, "")
  if (uuid) return uuid
  return `${capturedAt.toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}

function enqueue(
  name: string,
  message: string,
  stack: string,
  level: Level,
  ctx?: ReporterContext,
  capturedAt?: number
) {
  if (typeof window === "undefined") return
  // Not initialised yet — park it rather than drop it, and remember WHEN it was
  // captured. Drained by initErrorReporter(). See PendingCapture above.
  if (!initialized) {
    // Evict the OLDEST when full, matching QUEUE_HARD_CAP below: the newest
    // events are the more diagnostic ones. Refusing new entries instead meant a
    // burst of boot-time toasts could fill the buffer and shut out the outage
    // summary that followed them.
    if (pending.length >= PENDING_MAX) pending.shift()
    pending.push({ name, message, stack, level, ctx, at: capturedAt ?? Date.now() })
    return
  }
  if (!enabled) return
  if (halted && !ctx?.bypassHalt) return
  if (inDemoMode()) return

  const trimmedStack = trimStack(stack)
  const fp = clientFingerprint(name, trimmedStack || message, ctx?.component || ctx?.routeTag)
  if (!ctx?.skipDedup && shouldSuppress(fp)) return

  const pathname = safe(() => window.location.pathname, "/")
  const route = ctx?.route || pathname
  const persona = ctx?.persona || detectPersona(route)
  const fullUrl = safe(() => window.location.href, "")

  // Snapshot the device context here (not at flush) so events fired in the
  // 3s flush window carry the values current at the moment of the crash.
  // On web `dev` is { is_native: false }; on native it's populated once the
  // @capacitor/device bridge resolves — before that, only `is_native: true`
  // is set, which is still the most-useful bit for filtering "APK-only" bugs.
  const dev = getDeviceContext()

  const evt: QueuedEvent = {
    event_id: makeEventId(capturedAt ?? Date.now()),
    name: truncate(name, 200),
    message: truncate(message, 4000),
    stack: trimmedStack,
    level,
    release,
    url: truncate(fullUrl, 1000),
    route: truncate(route, 300),
    persona,
    recent_routes: getRecentRoutes(),
    // Snapshot here (not at flush) so the trail reflects state at the moment
    // of the crash even if new crumbs accumulate during the 3-s flush window.
    breadcrumbs: getBreadcrumbs(),
    user_agent: userAgent,
    request_id: ctx?.requestId,
    // Component identifier — prefer the explicit `component` field on the
    // context; fall back to `routeTag` for callers that haven't migrated.
    component: ctx?.component || ctx?.routeTag,
    // Capture network state at the moment of the error, not at flush —
    // the user may reconnect in the 3s flush window.
    network_status: snapshotNetworkStatus(),
    is_native: dev.is_native,
    device_manufacturer: dev.device_manufacturer,
    device_model: dev.device_model,
    device_os_version: dev.device_os_version,
    webview_version: dev.webview_version,
    app_version: dev.app_version,
    is_virtual: dev.is_virtual,
    android_sdk_int: dev.android_sdk_int,
    // Stamped at capture, not at flush and not at ingest. This is the only
    // moment that reflects when the thing actually happened. `capturedAt` is
    // set when this event was buffered pre-init and is being replayed, so a
    // replay keeps the original moment instead of the drain time.
    client_ts: capturedAt ?? Date.now(),
    // Read HERE, while the trader is (still) logged in. Reading it at flush or
    // drain time would be too late — logout has cleared `localStorage.user` by
    // then, which is the whole reason this field exists.
    client_user_id: currentUserId(),
    outage: ctx?.outage,
    net_journal: ctx?.netJournal,
    recovery_outcome: ctx?.recoveryOutcome,
    __fingerprint: fp,
  }

  // Hard cap protection against pathological loops (e.g. an error inside
  // a render that keeps re-rendering). Dedup blocks the SAME error from
  // requeuing, but distinct errors fired faster than the flush schedule
  // could still grow the queue. Trim the OLDEST events first — newer
  // events are more likely to reflect the current state.
  if (queue.length >= QUEUE_HARD_CAP) {
    queue.shift()
  }
  queue.push(evt)
  if (queue.length >= QUEUE_SOFT_LIMIT || level === "error" || ctx?.urgent) {
    // Errors flush eagerly; we'd rather pay one extra network roundtrip
    // than risk the page unloading with the error still in the queue.
    flushNow(false)
  } else {
    scheduleFlush()
  }
}

export function captureException(error: unknown, ctx?: ReporterContext): void {
  // Only bail on an explicit opt-out. Before init we don't yet know whether the
  // reporter is enabled, and dropping here would defeat the pre-init buffer in
  // enqueue() — which is the whole point of it.
  if (initialized && !enabled) return
  try {
    let name = "Error"
    let message = "Unknown error"
    let stack = ""

    if (error instanceof Error) {
      name = error.name || "Error"
      message = error.message || String(error)
      stack = error.stack || ""
    } else if (typeof error === "string") {
      message = error
    } else if (error && typeof error === "object") {
      const anyErr = error as { name?: string; message?: string; stack?: string }
      if (anyErr.name) name = String(anyErr.name)
      if (anyErr.message) message = String(anyErr.message)
      if (anyErr.stack) stack = String(anyErr.stack)
    }

    if (isDroppedMessage(message)) return
    if (isDroppedSource(stack)) return

    enqueue(name, message, stack, "error", ctx)
  } catch {
    // Never let the reporter's own failure feed back into itself.
  }
}

export function captureMessage(
  message: string,
  level: Level = "warn",
  ctx?: ReporterContext
): void {
  // See captureException — pre-init calls must reach enqueue()'s buffer. This
  // is the path connectivity_outage summaries travel on, and resolveOutage()
  // has already cleared its durable copy by the time it calls us.
  if (initialized && !enabled) return
  try {
    if (typeof message !== "string" || !message) return
    if (isDroppedMessage(message)) return
    // Synthesize a stack at the call site so triage in Kibana can see WHERE
    // the captureMessage was fired from (api.ts for 5xx, etc.), not just an
    // "unknown" top frame. The first frame is the Error() itself which we
    // discard inside the parser; the next frame is the actual caller.
    let stack = ""
    try {
      stack = new Error().stack || ""
    } catch {
      /* engine without Error.stack — leave empty */
    }
    enqueue("Message", message, stack, level, ctx)
  } catch {
    /* swallow */
  }
}

// Re-export the breadcrumb API so application code only needs to know about
// this one module. Mirrors Sentry's `Sentry.addBreadcrumb(...)` ergonomics.
export { addBreadcrumb } from "./breadcrumbs"
export type { Breadcrumb, BreadcrumbCategory } from "./breadcrumbs"

// ─── Init / global handlers ──────────────────────────────────────────────────
function onWindowError(ev: ErrorEvent): void {
  // Some browsers fire ErrorEvent with both `error` (the Error object) and
  // a separate `message`+`filename`+`lineno`. Prefer the Error if available.
  if (ev.error instanceof Error) {
    captureException(ev.error)
    return
  }
  // Cross-origin script errors fire with message "Script error." and no
  // detail — the drop list filters these because they have no stack to
  // act on, and adding `crossorigin="anonymous"` to chunk tags is the
  // proper fix (next.config.js).
  const msg = ev.message || ""
  if (!msg) return
  const synthStack = ev.filename
    ? `${msg}\n    at ${ev.filename}:${ev.lineno || 0}:${ev.colno || 0}`
    : msg
  if (isDroppedMessage(msg) || isDroppedSource(ev.filename)) return
  enqueue("WindowError", msg, synthStack, "error")
}

function onUnhandledRejection(ev: PromiseRejectionEvent): void {
  const reason: unknown = ev.reason
  // Aborts are intentional caller cancellations — ignore.
  if (reason && typeof reason === "object" && (reason as { name?: string }).name === "AbortError") {
    return
  }
  if (reason instanceof Error) {
    captureException(reason, { routeTag: "unhandled_rejection" })
    return
  }
  // Promise rejected with a non-Error value (string, object). Sentry-style
  // formatting so the message is at least searchable.
  const message =
    typeof reason === "string"
      ? reason
      : safe(() => JSON.stringify(reason), "[unserialisable rejection]")
  enqueue("UnhandledRejection", String(message), "", "error", { routeTag: "unhandled_rejection" })
}

function onPageHide(): void {
  // The page is going away — flush whatever is queued via sendBeacon so
  // we don't lose the last few events. Also make a best-effort beacon pass
  // over the outbox; entries stay put because a beacon can't confirm delivery.
  flushNow(true)
  drainOutbox(true)
}

function onOnline(): void {
  // The device says it has a link again. That is NOT proof our origin is
  // reachable — but the page is alive here, so fetch mode can find out for
  // itself: entries are removed only on a confirmed response.
  drainOutbox(false)
}

function onSessionExpired(): void {
  // Once the session expires, the page becomes a logout cascade — every
  // background fetch starts 401-ing. Stop reporting to keep the noise
  // out of ELK; the underlying issue isn't a frontend error.
  halted = true
  // Drop the breadcrumb trail too — a subsequent re-login on the same tab
  // must not inherit the previous user's interaction history.
  clearBreadcrumbs()
}

/**
 * Initialize the reporter. Safe to call multiple times — only the first
 * call wires up listeners. Designed to run inside `requestIdleCallback`
 * so it never blocks first paint.
 */
export function initErrorReporter(opts: InitOptions = {}): void {
  if (initialized) return
  if (typeof window === "undefined") return

  endpoint = opts.endpoint || endpoint
  release = opts.release || release
  enabled = opts.enabled !== false
  userAgent = safe(() => navigator.userAgent || "", "")

  // Populate device context. `is_native` is set synchronously inside this
  // call — subsequent async plugin work fills the granular details. We do
  // NOT await: init must never block, and events fired before the plugin
  // bridge resolves still carry `is_native` which is the most-useful facet.
  void initDeviceContext()

  // Listeners — passive registrations, never thrown from.
  window.addEventListener("error", onWindowError)
  window.addEventListener("unhandledrejection", onUnhandledRejection)
  window.addEventListener("pagehide", onPageHide)
  // Some browsers (older Safari) fire visibilitychange instead.
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      flushNow(true)
      drainOutbox(true)
    }
  })
  // Drain any outbox entries left over from a prior session (browser closed
  // while offline, tab reloaded mid-outage, etc). sendBeacon is fire-and-
  // forget — if we're still offline now this no-ops cleanly.
  window.addEventListener("online", onOnline)
  // Stop reporting once the session-expiry cascade starts (dispatched by
  // lib/api.ts on 401 / "session expired" responses).
  window.addEventListener("session-expired", onSessionExpired)

  // One-shot drain on init — flushes events left over from the last time the
  // user was on this origin. Fetch mode: the page is alive, so delivery is
  // confirmed rather than assumed. This is the path that finally lands the
  // reports from an outage the user "escaped" by reloading.
  drainOutbox(false)

  initialized = true

  // Replay anything captured during the pre-init window. Must run AFTER
  // `initialized = true`, or enqueue() would simply re-buffer each one. Each
  // event carries its original capture time so client_ts — and therefore
  // @timestamp in Kibana — still reflects when it happened.
  if (pending.length > 0) {
    const replay = pending
    pending = []
    for (const p of replay) {
      safe(() => enqueue(p.name, p.message, p.stack, p.level, p.ctx, p.at), undefined)
    }
  }
}

/**
 * Deliver anything still parked in the outbox, called once connectivity has
 * been positively confirmed (see lib/net-journal.ts). Distinct from the
 * `online` event, which fires on a signal that was true throughout the very
 * incident this exists to capture.
 */
export function drainOutboxNow(): void {
  drainOutbox(false)
}
